/**
 * Recover the app when a dynamic import fails because the chunk cannot be
 * loaded (typical after a new deploy leaves the user on a stale index.html
 * that references old hashed filenames, or while Cloudflare is still
 * propagating a deployment and answers a chunk URL with the SPA fallback).
 *
 * Vite emits `vite:preloadError` for these failures. We reload to pick up the
 * fresh index.html; a sessionStorage record guards against an infinite reload
 * loop if the failure is not actually stale-deploy.
 *
 * A plain reload is not enough when the chunk failed because a service-worker
 * cache holds the SPA HTML fallback under the chunk's .js URL (OWA-691): the
 * reload is served from that same poisoned cache and fails identically, which
 * is exactly the "chunk load failed again after recent reload" loop seen on
 * beta on 2026-07-23. So the FIRST recovery attempt purges poisoned cache
 * entries before reloading.
 *
 * OWA-828: one purge-and-reload plus one reload was not enough either. A tab
 * that reloads inside the roughly 30-second window after a deploy's edge purge
 * fetches HTML for a chunk that WILL be live seconds later, and the old code
 * gave up to the error boundary after the second attempt. Recovery is now a
 * bounded schedule with backoff: purge-and-reload at once, then again after
 * 5s, 10s and 20s. Propagation gaps resolve well inside that; a chunk that is
 * genuinely gone still surfaces the error boundary, about a minute in.
 *
 * EVERY attempt purges, not just the first (CXR-1-004): a new worker's precache
 * install can still be in flight when the first sweep runs, and an install
 * fetch that completes afterwards writes the HTML fallback into the precache
 * under the chunk's URL. A plain reload then re-reads that entry forever. So
 * each retry sweeps again, after its backoff, right before it reloads.
 */
import {captureException} from './picos/error';
import {purgePoisonedAssets} from './cachePurge';

/** sessionStorage key holding the current recovery episode. */
export const RECOVERY_KEY = 'outlaw_chunk_recovery';

/**
 * Delay before each attempt, in order. Every attempt purges poisoned caches
 * and then reloads; attempt 1 does so at once, the rest after backoff.
 */
export const RECOVERY_SCHEDULE_MS = [0, 5_000, 10_000, 20_000] as const;

export const MAX_ATTEMPTS = RECOVERY_SCHEDULE_MS.length;

/**
 * A failure this long after the episode began is treated as a NEW episode
 * (the full schedule is available again). Longer than the schedule plus the
 * reloads it implies, so a give-up is sticky for the rest of that minute and
 * a later, unrelated stale-chunk hit still gets recovered.
 */
export const EPISODE_WINDOW_MS = 120_000;

export type RecoveryRecord = {
    /** Attempts already made in this episode. */
    attempts: number;
    /** When the first attempt of this episode was made. */
    firstAt: number;
    /** When the most recent attempt was made. */
    lastAt: number;
};

export type RecoveryAction =
    | {kind: 'purge-and-reload'; attempt: number; delayMs: number}
    | {kind: 'give-up'; attempt: number; delayMs: 0};

export function readRecoveryRecord(): RecoveryRecord | null {
    try {
        const raw = sessionStorage.getItem(RECOVERY_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<RecoveryRecord>;
        if (typeof parsed.attempts !== 'number' || typeof parsed.firstAt !== 'number') return null;
        return {attempts: parsed.attempts, firstAt: parsed.firstAt, lastAt: parsed.lastAt ?? parsed.firstAt};
    }
    catch {
        return null;
    }
}

function writeRecoveryRecord(record: RecoveryRecord): void {
    sessionStorage.setItem(RECOVERY_KEY, JSON.stringify(record));
}

/**
 * Which recovery attempt comes next, given the episode so far. Pure, so every
 * branch is testable: `reloadForStaleChunk` itself reloads the test harness on
 * all but the 'give-up' branch.
 *
 * @param now     Current time (ms).
 * @param record  The episode so far; `null` or an expired episode starts fresh.
 */
export function nextRecoveryAction(now: number, record: RecoveryRecord | null = readRecoveryRecord()): RecoveryAction {
    const fresh = !record || now - record.firstAt > EPISODE_WINDOW_MS;
    const attempt = fresh ? 1 : record.attempts + 1;

    if (attempt > MAX_ATTEMPTS) return {kind: 'give-up', attempt, delayMs: 0};
    return {kind: 'purge-and-reload', attempt, delayMs: RECOVERY_SCHEDULE_MS[attempt - 1]};
}

export function isChunkLoadError(err: unknown): boolean {
    const message = err instanceof Error ? err.message : String(err ?? '');
    return (
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('error loading dynamically imported module')
    );
}

/**
 * Side-effect seams, replaceable by tests. Every reload path goes through
 * `reload`, so the recovery logic can be exercised in a browser without the
 * harness page actually reloading.
 */
export const recoveryEffects = {
    reload: (): void => window.location.reload(),
    schedule: (fn: () => void, ms: number): number => window.setTimeout(fn, ms),
    cancel: (handle: number): void => window.clearTimeout(handle),
    purge: (): Promise<string[]> => purgePoisonedAssets(),
};

/** What the UI can show while a retry is pending. */
export type PendingRecovery = {attempt: number; maxAttempts: number; delayMs: number} | null;

let pending: PendingRecovery = null;
let pendingHandle: number | null = null;
const listeners = new Set<() => void>();

function setPending(next: PendingRecovery): void {
    pending = next;
    listeners.forEach(fn => fn());
}

/** Snapshot for `useSyncExternalStore`; stable reference until it changes. */
export const getPendingRecovery = (): PendingRecovery => pending;

export function subscribePendingRecovery(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/**
 * Wipe the episode so the next failure gets the full schedule again, and cancel
 * any reload already scheduled. Called by the error boundary's Reload Now: a
 * user-initiated reload must always be a real, fresh attempt (OWA-828), never a
 * no-op inside a cooldown.
 */
export function resetRecovery(): void {
    sessionStorage.removeItem(RECOVERY_KEY);
    if (pendingHandle !== null) {
        recoveryEffects.cancel(pendingHandle);
        pendingHandle = null;
    }
    setPending(null);
}

/** Reset the episode and reload. The one path the Reload Now button takes. */
export function reloadNow(): void {
    resetRecovery();
    recoveryEffects.reload();
}

/**
 * Attempt recovery for a chunk-load failure. Returns true when a reload has
 * been performed or scheduled (the caller should suppress the error), false
 * when the schedule is exhausted and the error should surface.
 */
export function reloadForStaleChunk(err: unknown, source: string): boolean {
    // The same failure reaches us twice (unhandledrejection AND the error
    // boundary's onError). One scheduled reload is enough; do not burn two
    // attempts on it.
    if (pending) return true;

    const now = Date.now();
    const record = readRecoveryRecord();
    const action = nextRecoveryAction(now, record);

    if (action.kind === 'give-up') {
        // Schedule exhausted inside the episode window. Let the error surface
        // so the user sees the error frame, whose Reload Now resets the episode.
        console.error(`[chunkReload] ${source}: chunk load failed again after ${MAX_ATTEMPTS} recovery attempts`, err);
        captureException(err instanceof Error ? err : new Error(String(err)), {
            tags: ['chunkReload', 'post-reload-failure'],
            extra: {source, attempts: record?.attempts ?? 0},
        });
        return false;
    }

    const fresh = action.attempt === 1;
    writeRecoveryRecord({
        attempts: action.attempt,
        firstAt: fresh || !record ? now : record.firstAt,
        lastAt: now,
    });
    setPending({attempt: action.attempt, maxAttempts: MAX_ATTEMPTS, delayMs: action.delayMs});

    // Drop any cache entry holding HTML under a code-asset URL, then reload.
    // Without the purge the reload re-reads the same poisoned entry. The purge
    // is async but never rejects; we reload once it settles so the fresh page
    // cannot race the deletes.
    const purgeThenReload = () => {
        pendingHandle = null;
        void recoveryEffects.purge()
            .then(purged => {
                if (purged.length) console.warn(`[chunkReload] purged ${purged.length} poisoned cache entr${purged.length === 1 ? 'y' : 'ies'}`, purged);
            })
            .finally(() => recoveryEffects.reload());
    };

    if (action.delayMs === 0) {
        console.warn(`[chunkReload] ${source}: chunk load failed, purging poisoned caches then reloading (attempt ${action.attempt} of ${MAX_ATTEMPTS})`, err);
        purgeThenReload();
        return true;
    }

    console.warn(`[chunkReload] ${source}: chunk still failing, purging and reloading again in ${action.delayMs / 1000}s (attempt ${action.attempt} of ${MAX_ATTEMPTS})`, err);
    pendingHandle = recoveryEffects.schedule(purgeThenReload, action.delayMs);
    return true;
}

export function installChunkReloadHandler(): void {
    window.addEventListener('vite:preloadError', (event) => {
        if (reloadForStaleChunk(event.payload, 'vite:preloadError')) {
            event.preventDefault();
        }
    });

    window.addEventListener('unhandledrejection', (event) => {
        if (isChunkLoadError(event.reason) && reloadForStaleChunk(event.reason, 'unhandledrejection')) {
            event.preventDefault();
        }
    });
}
